import { useEffect, useMemo, useState } from 'react';
import { useQueries } from '@tanstack/react-query';
import { previewProductCost } from '../api/products';
import { getErrorMessage } from '../api/client';
import { useBusinessUuid } from '../useBusinessUuid';

const DEBOUNCE_MS = 400;

export const COST_PREVIEW_STATUS = {
  IDLE: 'idle',
  LOADING: 'loading',
  ERROR: 'error',
  READY: 'ready',
};

// Only a linked line with a usable quantity and cost can be previewed; any
// other line has no new cost to show yet.
const toPreviewLine = (item, quantityField) => {
  const productUuid = item.selectedItem?.uuid;
  if (!productUuid) return null;
  const quantity = parseFloat(item[quantityField]);
  const unitCost = parseFloat(item.unitCost);
  if (!Number.isFinite(quantity) || quantity <= 0) return null;
  if (!Number.isFinite(unitCost) || unitCost < 0) return null;
  return {
    productUuid,
    costMode: item.costMode || 'update',
    unitCost,
    quantity,
  };
};

const sameLine = (a, b) =>
  a === b ||
  (a != null &&
    b != null &&
    a.productUuid === b.productUuid &&
    a.costMode === b.costMode &&
    a.unitCost === b.unitCost &&
    a.quantity === b.quantity);

/**
 * Previews the New Cost of every linked line through the backend, so the value
 * shown is exactly the unitPrice the line will leave on its product.
 *
 * Each line is its own query: editing one line refetches only that line, and
 * lines that did not change are served from cache. Inputs are debounced so
 * typing a quantity does not fire a request per keystroke.
 *
 * Returns a Map of line id to `{ status, newCost, error }`.
 */
export function useCostPreview(items, quantityField) {
  const businessUuid = useBusinessUuid();

  const lines = useMemo(
    () =>
      items.map((item) => ({
        id: item.id,
        line: toPreviewLine(item, quantityField),
      })),
    [items, quantityField],
  );

  const [debouncedLines, setDebouncedLines] = useState(lines);
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedLines(lines), DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [lines]);

  const queries = useQueries({
    queries: debouncedLines.map(({ line }) => ({
      queryKey: ['products', 'cost-preview', businessUuid, line],
      queryFn: () => previewProductCost([line]).then((rows) => rows[0]),
      enabled: Boolean(line && businessUuid),
      retry: false,
    })),
  });

  return useMemo(() => {
    const settledById = new Map(
      debouncedLines.map(({ id, line }, index) => [id, { line, query: queries[index] }]),
    );
    const previews = new Map();
    lines.forEach(({ id, line }) => {
      if (!line) {
        previews.set(id, { status: COST_PREVIEW_STATUS.IDLE, newCost: null, error: null });
        return;
      }
      const settled = settledById.get(id);
      if (!settled || !sameLine(settled.line, line) || settled.query.isPending) {
        previews.set(id, { status: COST_PREVIEW_STATUS.LOADING, newCost: null, error: null });
        return;
      }
      if (settled.query.isError) {
        previews.set(id, {
          status: COST_PREVIEW_STATUS.ERROR,
          newCost: null,
          error: getErrorMessage(settled.query.error),
        });
        return;
      }
      previews.set(id, {
        status: COST_PREVIEW_STATUS.READY,
        newCost: settled.query.data?.newCost ?? null,
        error: null,
      });
    });
    return previews;
  }, [lines, debouncedLines, queries]);
}
